/// <summary>
/// The Halted tick type indicates if a contract has been halted for trading.
/// </summary>
export enum Halted {
  NA = -1,
  NotHalted = 0,
  GeneralHalt = 1,
  VolatilityHalt = 2
}

export enum OrderStatusType {
  PendingSubmit = 0,
  PendingCancel = 1,
  PreSubmitted = 2,
  Submitted = 3,
  ApiCancelled = 4,
  Cancelled = 5,
  Filled = 6,
  Inactive = 7
}

export enum BarSize {
  NA = 0,
  _1_SEC = 1,
  _5_SEC = 2,
  _10_SEC = 3,
  _15_SEC = 4,
  _30_SEC = 5,
  _1_MIN = 6,
  _2_MIN = 7,
  _3_MIN = 8,
  _5_MIN = 9,
  _10_MIN = 10,
  _15_MIN = 11,
  _20_MIN = 12,
  _30_MIN = 13,
  _1_HR = 14,
  _2_HRS = 15,
  _3_HRS = 16,
  _4_HRS = 17,
  _8_HRS = 18,
  _1_DAY = 19,
  _1_WEEK = 20,
  _1_MONTH = 21
}

export enum MacroContextType {
  Period = 0,
  Event = 1
}

export enum MacroTrendType {
  Volatile = 0,
  Bullish = 1,
  Bearish = 2
}

export enum PeriodMonthPart {
  Beginning = 0,
  End = 1,
  Full = 2
}

export enum SqueezeResult {
  NA = 0,
  Inside = 1,
  Outside = 2
}

export enum MacroLinkType {
  SecuritySpecific = 0,
  General = 1
}

export enum MarketTradingHours {
  Closed = 0,
  Open = 1,
  OpenLiquid = 2
}

export enum SecurityCategory {
  Stock = 0,
  Futures = 1,
  MicroFutures = 2,
  Shortlist = 3
}

export enum SecuritySector {
  Unspecificed = 0,
  Indexes = 1,
  Tech = 2,
  Financial = 3,
  Healthcare = 4,
  Industrial = 5,
  RealEstate = 6,
  Energies = 7,
  Environmental = 8,
  Metals = 9,
  Softs = 10,
  Currency = 11,
  Holding = 12,
  Etf = 13,
  ConsumerGoods = 14
}

export enum ThresholdAlertType {
  None = 0,
  Above = 1,
  Below = 2
}

export enum TrendLineType {
  Custom = 0,
  Resistance = 1,
  Support = 2,
  MidRange = 3,
  Percent = 4
}

export enum WhatToShow {
  TRADES = 0,
  MIDPOINT = 1,
  BID = 2,
  ASK = 3,
  BID_ASK = 4
}

export enum StrokeType {
  Solid = 0,
  Dotted = 3,
  Dashed = 8
}

export enum LineWidth {
  Narrow = 1,
  Standard = 2,
  Fat = 3
}

export enum VolatilityRegime {
  NA = 0,
  Low = 1,
  Normal = 2,
  High = 3
}

export enum VolatilityRegimeStatus {
  Unchanged = 0,
  Increased = 1,
  Decreased = 2
}

export enum LegOrderType {
  Limit = 0,
  Market = 1,
  RthMarket = 2
}

export enum ProfitDirection {
  None = 0,
  Up = 1,
  Down = 2
}

export enum StrategyTaskStatus {
  Unchanged = 0,
  Averaging = 1,
  AveragingFull = 2,
  ScaleOut = 3,
  ScaleOutExhausted = 4,
  RebuyExhaustedLadder = 5,
  Reset = 6,
  LadderSizeOutOfBounds = 7,
  ProtectHighRisk = 8,
  MaxFuturesPositionsReached = 9,
  Recentering = 10,
  Initiate = 11,
  Cleanup = 12,
  Close = 13,
  Wait = 14,
  ProtectDrift = 15,
  Repair = 16,
  SkewEntry = 17,
  CreateStopOrder = 18,
  UpdateStopOrder = 19,
  CancelStopOrder = 20,
  ScaleOutStdExitThreshold = 21,
  ProfitTargetReached = 22,
  PositionTimeout = 23,
  RaceFinished = 24,
  RedlineBreakoutCutOff = 25,
  RedlineBreakoutFailed = 26,
  MicroHarvestReached = 27,
  RedlineReached = 28,
  MarketClosed = 29,
  SpreadSwap = 31,
  MaxAttemptsReached = 32,
  SpreadStopLoss = 33,
  CorrectionTriggered = 34,
  ResetFormationOnLoss = 35,
  TrailingKeepWinningSide = 36,
  TrailingCutLoosingSide = 37,
  TrailingCancelOrder = 38,
  TrailingCreateStopOrder = 39,
  TrailingUpdateStopOrder = 40,
  TrailingStopLoss = 41
}

export enum MarketPriceType {
  Mid = 0,
  Ask = 1,
  Bid = 2
}

export enum StrategyType {
  HotLap = 1,
  Redline = 3,
  Slipstream = 4,
  Pitlane = 5,
  Overtake = 6,
  Chicane = 7,
  Apex = 8,
  Pursuit = 9
}

export enum SlipstreamResult {
  None = 0,
  StopLoss = 1,
  ProfitTarget = 2,
  // dead - was the micro-harvest result, kept so historical documents still deserialize/display correctly
  ExitSignal = 3,
  PartialFill = 4,
  TrailingStopLoss = 5
}

export enum ApexResult {
  None = 0,
  StopLoss = 1,
  ProfitTarget = 2,
  ExitSignal = 3,
  PartialFill = 4,
  TrailingStopLoss = 5
}

export enum ScheduleState {
  Auto = 0,
  Restrictive = 1,
  Manually = 2,
  Archived = 3,
  Paused = 4
}

export enum LegOrderAction {
  Buy = 0,
  Sell = 1
}

export enum ManualTaskAction {
  None = 0,
  Reset = 1,
  Close = 2,
  Start = 3,
  Execute = 4
}

export enum InstrumentType {
  Contract = 0,
  TradingClass = 1,
  Option = 2
}

export enum RaceHarvestType {
  Classic = 0,
  Turbo = 1,
  Smooth = 2
}

export enum ContractType {
  Underlying = 0,
  Option = 1
}

export enum SpreadSide {
  Primary = 0,
  Counter = 1
}

export enum RacingPhase {
  Unknown = 0,
  Ready = 1,
  Pending = 2,
  Racing = 3,
  Finished = 4,
  Formation = 5,
  Resetting = 6,
  PitlanePending = 7,
  Pitlane = 8,
  Overtaking = 9
}

export enum OvertakeResult {
  None = 0,
  ProfitTargetReached = 1,
  TrailingStopLoss = 2,
  StopLoss = 3
}

export enum PursuitResult {
  None = 0,
  ProfitTargetReached = 1,
  TrailingStopLoss = 2,
  StopLoss = 3
}

export enum ChicaneRaceState {
  Unknown = 0,
  Hedged = 1,
  PendingHedgeProfit = 2,
  Naked = 4
}

export enum RedlineFormationStatus {
  NotReady = 0,
  MicroHarvestReached = 1,
  NoTrendEfficiency = 2,
  NoProfitContinuation = 3, // deprecated
  ProfitSideReached = 4, // deprecated
  LossSideReached = 5, // deprecated
  PositionTimeout = 6,
  RedlineRacingDisabled = 7,
  StartRace = 8,
  ResetFormationOnLoss = 9,
  StopLoss = 10,
  ProfitTargetReached = 11,
  TrailingStopLoss = 12
}

export enum RedlineRacingStatus {
  NotStarted = 0,
  Started = 1,
  ProfitTargetReached = 2,
  Failed = 3,
  PositionTimeout = 4, // deprecated
  Disabled = 5,
  TrailingStopLoss = 6 // deprecated
}

export enum VixState {
  NoData = 0,
  Compressed = 1,
  Normal = 2,
  Elevated = 3,
  Extreme = 4
}

export enum TrendEfficiencyState {
  NoData = 0,
  Ranging = 1,
  Bullish = 2,
  Bearish = 3
}

export enum RegimePhase {
  NoData = 0,
  Early = 1,
  Mid = 2,
  Late = 3
}

// XLP/SPX ratio direction, named for what it represents (capital rotating in/out of defensives) rather than
// reusing TrendEfficiencyState's Bullish/Bearish/Ranging - those read as market direction/chart shape, which
// don't fit here: MoneyIn (ratio rising) is a risk-off signal, and NoFlow means "no net rotation detected".
export enum CapitalFlowState {
  NoData = 0,
  NoFlow = 1,
  MoneyIn = 2,
  MoneyOut = 3
}

/** @deprecated Replaced by TrendEfficiencyState (direction) + RegimePhase (phase). No longer populated by the backend. */
export enum RegimeState {
  NoData = 0,
  WeakRanging = 1,
  LateWeakRanging = 2,
  StrongRanging = 3,
  LateStrongRanging = 4,
  WeakBullish = 5,
  StrongBullish = 6,
  WeakBearish = 7,
  StrongBearish = 8
}

export enum MarketPriceSide {
  Bid = 0,
  Ask = 1
}

export enum SpreadRegime {
  Unknown = 0,
  MeanRevert = 1,
  DriftPrimary = 2,
  DriftCounter = 3,
  Mixed = 4
}

export enum SpreadMomentum {
  Unknown = 0,
  Passive = 1,
  Building = 2,
  Mixed = 3,
  Softening = 4,
  Exhausting = 5
}
